import type {
  AssertionFailure,
  PlaygroundTestRequest,
  PlaygroundTestResponse,
  TestAssertion,
  TestCaseResult,
  TestRunResponse,
  TestSuiteCase,
} from "@/types/api";
import { detectSchemaEncoding } from "./schemaEncoding";
import { TEST_OPERATOR_LABELS } from "./stagingModel";

// Running a staged script row reuses /studio's playground endpoint
// (POST /transform/run): one script, the Studio's input document as the
// payload, the block's schema when one is attached. The result lands in
// the Output panel as a read-only document.

/** What the Output panel shows for the latest run. */
export interface ScriptRunOutput {
  scriptName: string;
  success: boolean;
  /** Pretty-printed result document, or the failure message. */
  text: string;
  /** Present on test runs: the Output panel renders the friendly results
   *  view (verdict, per-case rows, failure cards) instead of raw text. */
  testRun?: TestRunView;
}

/** One assertion line of a case's Output view — the assertion as the
 *  editor writes it (path op expected) with its own verdict; failed
 *  lines carry the received value. */
export interface TestRunAssertionView {
  path: string;
  /** The operator glyph the assertion editor shows (==, >, contains…). */
  op: string;
  expected: string;
  passed: boolean;
  /** What the pipeline actually produced — failed lines only. */
  received?: string;
}

/** One case's verdict inside a test run's Output view. */
export interface TestRunCaseView {
  name: string;
  passed: boolean;
  /** The case's runtime error — set when it never reached assertions. */
  error?: string;
  /** Every assertion that ran, in editor order — passed lines included
   *  (the engine reports failures only; caseView merges the sent case
   *  back in). Empty on runtime errors: nothing ran. */
  assertions: TestRunAssertionView[];
}

/** A test run's structured result for the Output panel. */
export interface TestRunView {
  cases: TestRunCaseView[];
  passed: number;
  total: number;
  /** A single-case run's final document, pretty-printed — what the
   *  assertions ran against; suite runs stay compact without it. */
  actual?: string;
}

// An assertion value for the Expected/Received lines — JSON form when it
// has one (Jest's convention), the raw string otherwise.
function displayValue(value: unknown): string {
  return JSON.stringify(value) ?? String(value);
}

const failedLine = (f: AssertionFailure): TestRunAssertionView => ({
  path: f.path,
  op: TEST_OPERATOR_LABELS[f.assert_type],
  expected: displayValue(f.expected),
  passed: false,
  received: displayValue(f.actual),
});

// A sent assertion and the engine's next pending failure describe the same
// line when path, operator and expected value all agree (expected compared
// via the same display form the Expected/Received lines use).
function matchesSentLine(f: AssertionFailure, a: TestAssertion): boolean {
  return (
    f.path === a.path &&
    f.assert_type === a.assert_type &&
    displayValue(f.expected) === displayValue(a.expected_value)
  );
}

// A sent line the engine did not report as a failure: it ran and held.
function passedLine(a: TestAssertion): TestRunAssertionView {
  return {
    path: a.path,
    op: TEST_OPERATOR_LABELS[a.assert_type],
    expected: displayValue(a.expected_value),
    passed: true,
  };
}

// The engine reports only the FAILED assertions, in the order they were
// sent (its checker filter_maps the case's list), so the per-line verdict
// view pairs the sent assertions with that failure subsequence: one
// forward walk, consuming a failure when path/op/expected match the sent
// line. Anything the engine added beyond the sent lines (strict-mode
// extra-field failures) trails as failed lines of its own.
function assertionViews(
  sent: readonly TestAssertion[],
  failures: readonly AssertionFailure[],
): TestRunAssertionView[] {
  let next = 0;
  const lines = sent.map((a): TestRunAssertionView => {
    const f = failures[next];
    if (f === undefined || !matchesSentLine(f, a)) return passedLine(a);
    next++;
    return failedLine(f);
  });
  return [...lines, ...failures.slice(next).map(failedLine)];
}

// One case's verdict for the Output panel's structured view; a failed
// case carries its runtime error and/or the assertion lines with their
// verdicts. The engine omits empty fields wholesale (serde
// skip_serializing_if), so a passing case arrives with NO failures key
// at all. `sent` is the case as the run request carried it — without it
// (a result the client has no sent case for) only the failures show.
function caseView(
  result: TestCaseResult,
  sent?: readonly TestAssertion[],
): TestRunCaseView {
  return {
    name: result.name,
    passed: result.passed,
    error: result.error,
    // A runtime error means the assertions never ran — listing them as
    // passed would lie; the error card stands alone.
    assertions: result.error
      ? []
      : assertionViews(sent ?? [], result.failures ?? []),
  };
}

// The raw-text fallback for a failed case (the copyable `text` field).
function failureText(result: TestCaseResult): string {
  if (result.error) return result.error;
  return (
    (result.failures ?? []).map((f) => f.message).join("\n") || "Test failed"
  );
}

const noResultOutput = (name: string): ScriptRunOutput => ({
  scriptName: name,
  success: false,
  text: "The run returned no result",
});

/** The Output entry for one case verdict — the view carries the final
 *  document the assertions ran against; text stays as the raw fallback.
 *  `sent` is the case the run request carried, so passing assertions
 *  show alongside the failed ones. */
export function testRunOutput(
  name: string,
  result: TestCaseResult | undefined,
  sent?: TestSuiteCase,
): ScriptRunOutput {
  if (!result) return noResultOutput(name);
  return {
    scriptName: name,
    success: result.passed,
    text: result.passed ? "Test passed" : failureText(result),
    testRun: {
      cases: [caseView(result, sent?.assertions)],
      passed: result.passed ? 1 : 0,
      total: 1,
      actual:
        result.actual !== undefined
          ? JSON.stringify(result.actual, null, 2)
          : undefined,
    },
  };
}

/** The Output entry for a whole-suite run: one row per case over the
 *  summary count — green only when every case passed. `sentCases` are the
 *  cases the run request carried (matched back by name — unique within a
 *  suite), so each row lists its passing assertions too. */
export function suiteRunOutput(
  name: string,
  item: TestRunResponse["item"],
  sentCases?: readonly TestSuiteCase[],
): ScriptRunOutput {
  if (!item) return noResultOutput(name);
  const sentByName = new Map(
    (sentCases ?? []).map((c) => [c.name, c.assertions]),
  );
  return {
    scriptName: name,
    success: item.summary.failed === 0,
    text: `${item.summary.passed}/${item.summary.total} tests passed`,
    testRun: {
      cases: item.results.map((r) => caseView(r, sentByName.get(r.name))),
      passed: item.summary.passed,
      total: item.summary.total,
    },
  };
}

// Both run requests need the payload to already be JSON before they reach
// the engine — otherwise its error is opaque; catch it here with a message
// that points at the input document instead.
function assertValidPayload(payload: string): void {
  try {
    JSON.parse(payload);
  } catch {
    throw new Error("The input document is not valid JSON");
  }
}

// The schema half of a run request: the engine expects the body wrapped
// with its encoding, or no `schema` field at all when nothing is attached.
function schemaField(
  schemaBody: string | null | undefined,
): PlaygroundTestRequest["schema"] {
  return schemaBody
    ? {
        schema_encoding: detectSchemaEncoding(schemaBody) ?? "json",
        body: schemaBody,
      }
    : undefined;
}

/** Builds the run request; throws a user-facing message when the script
 *  or input document can't be run as they are. */
export function scriptRunRequest(options: {
  script: string;
  payload: string;
  schemaBody?: string | null;
}): PlaygroundTestRequest {
  const { script, payload, schemaBody } = options;
  // An empty script with a schema attached is the engine's documented
  // pass-through: the payload is projected through the schema only. With
  // neither, refuse in the engine's own words (what old /studio showed).
  if (script.trim() === "" && !schemaBody)
    throw new Error("Provide at least one script or a schema");
  assertValidPayload(payload);
  return {
    scripts: script.trim() === "" ? [] : [script],
    payload,
    schema: schemaField(schemaBody),
  };
}

/** Builds the Run-pipeline request: every staged transform chained in
 *  order — the engine feeds each stage's output to the next and applies
 *  the schema to the FINAL output only. Throws user-facing messages for
 *  unrunnable states. */
export function pipelineRunRequest(options: {
  scripts: string[];
  payload: string;
  schemaBody?: string | null;
}): PlaygroundTestRequest {
  const { scripts, payload, schemaBody } = options;
  // Script-less + schema = the engine's schema-only pass-through; with
  // neither, refuse in the engine's own words (what old /studio showed).
  if (scripts.length === 0 && !schemaBody)
    throw new Error("Provide at least one script or a schema");
  if (scripts.some((s) => s.trim() === ""))
    throw new Error("All scripts must be non-empty");
  assertValidPayload(payload);
  return {
    scripts,
    payload,
    schema: schemaField(schemaBody),
  };
}

// A pipeline stage broke: name it in the engine's own words when it sent
// one, otherwise fall back to naming the stage ourselves.
function failedStageOutput(
  scriptName: string,
  failedStage: number,
  message: string | undefined,
): ScriptRunOutput {
  return {
    scriptName,
    success: false,
    text: message || `Stage ${failedStage} failed`,
  };
}

/** The Output-panel view of a run response: the failed stage's message,
 *  or the last stage's document pretty-printed. */
export function scriptRunResult(
  scriptName: string,
  response: PlaygroundTestResponse,
): ScriptRunOutput {
  const failedStage = response.item?.failed_stage;
  if (failedStage !== undefined)
    return failedStageOutput(scriptName, failedStage, response.message);
  const stages = response.item?.stages;
  const last = stages?.[stages.length - 1];
  if (!last) {
    return {
      scriptName,
      success: false,
      text: response.message || "The run produced no output",
    };
  }
  return {
    scriptName,
    success: true,
    text: JSON.stringify(last.document, null, 2),
  };
}

/** The state update that ends a run: clears the in-flight id only while it
 *  is still this run's own — a completion must never wipe a newer run's
 *  in-flight state (that would re-enable its button mid-flight). */
export function clearRunId(runId: string) {
  return (prev: string | null): string | null => (prev === runId ? null : prev);
}
